'use client';

import * as React from 'react';
import { useDebouncedCallback, useLocalStorageValue } from '@react-hookz/web';
import { useMutation } from '@tanstack/react-query';
import { LikeIcon } from 'components/like-icon';
import { useTranslations } from 'next-intl';
import { api } from 'orpc/react';
import {
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'ui';

export type AsideActionProps = {
  postId: string;
  likeCount: number;
};

export function AsideActions({ postId, likeCount }: AsideActionProps) {
  const t = useTranslations('Blog.Post.Aside');
  const { value: likedPosts, set: setLikedPosts } = useLocalStorageValue<
    string[]
  >('likedPosts', {
    defaultValue: [],
  });
  const liked = likedPosts?.includes(postId);
  const { mutate } = useMutation(api.blog.likePost.mutationOptions());
  const initialLikeState = React.useRef<boolean | null>(null);
  const likePost = useDebouncedCallback(
    (currentLiked: boolean) => {
      if (
        initialLikeState.current !== null &&
        currentLiked === initialLikeState.current
      ) {
        // Only update the server if the user has changed their like state
        // skip if the user rolls back to the original state
        mutate({ postId, isLiked: currentLiked });
      }
      initialLikeState.current = null;
    },
    [],
    3000,
  );
  const [_likeCount, setLikeCount] = React.useState(likeCount);
  const handleLikeClick = () => {
    const nextState = !liked;
    if (initialLikeState.current === null) {
      initialLikeState.current = nextState;
    }
    if (liked) {
      setLikedPosts(likedPosts?.filter((id) => id !== postId) || []);
      setLikeCount((prev) => prev - 1);
    } else {
      setLikedPosts([...(likedPosts || []), postId]);
      setLikeCount((prev) => prev + 1);
    }
    likePost(nextState);
  };
  return (
    <div className="bg-primary">
      <Separator />
      <div className="flex min-w-[130px] items-center gap-2 pt-7">
        <TooltipProvider delayDuration={1000}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                id="like"
                type="button"
                className="group inline-flex items-center gap-1 rounded px-2 py-1 text-quaternary hover:bg-secondary-hover"
                aria-label={t('Like')}
                onClick={handleLikeClick}
              >
                <LikeIcon isLiked={!!liked} />
                {_likeCount > 0 ? (
                  <span className="text-sm font-medium">{_likeCount}</span>
                ) : null}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('Like')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}
